import React from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Button, InputWithIcon, PasswordInput } from '@quality24/design-system';
import {
  faEnvelope,
  faLock,
  faPenToSquare,
} from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import { IsFederatedUserPayload } from '../../../contexts/auth/authContext';
import { useAuth } from '../../../contexts/auth/useAuth';
import { isPasswordResetBlocked } from '../../../constants/passwordReset';

export interface Props {
  className?: string;
  initialValues?: FormValues;
  loading: boolean;
  onSubmit: (values: FormValues) => Promise<void>;
  onUpdatePassword: (username?: string) => void;
  onSubmitEmail?: (value: string, callback?: () => void) => void;
  userFederations: IsFederatedUserPayload[] | null;
}

export interface FormValues {
  email: string;
  password: string;
}

/**
 * Login form default values
 */
const defaultValues: FormValues = {
  email: '',
  password: '',
};

// The form validation schema
const validationSchema = yup.object({
  email: yup
    .string()
    .email('Insira um e-mail válido')
    .required('Campo obrigatório'),
  password: yup.string().required('Campo obrigatório'),
});

/**
 * <LoginForm> component
 */
const LoginForm: React.FunctionComponent<Props> = ({
  className,
  initialValues = {},
  loading,
  onSubmit,
  onUpdatePassword,
  onSubmitEmail,
  userFederations,
}) => {
  const [step, setStep] = React.useState<'email' | 'password'>('email');
  const { federatedSignIn } = useAuth();
  const {
    handleSubmit,
    values,
    errors,
    touched,
    setFieldTouched,
    setFieldValue,
  } = useFormik({
    initialValues: { ...defaultValues, ...initialValues },
    validationSchema,
    onSubmit,
  });

  const isFederatedUser = userFederations
    ? !!userFederations.length
    : undefined;

  const handleOnSubmit = React.useCallback(
    (evt: React.FormEvent<HTMLFormElement>) => {
      if (step === 'email') {
        onSubmitEmail?.(values.email, () => setStep('password'));
        evt.preventDefault();
        return;
      }
      handleSubmit(evt);
    },
    [handleSubmit, onSubmitEmail, step, values.email],
  );

  return (
    <form
      className={classNames(
        'd-flex flex-column flex-justify-between h-100',
        className,
      )}
      onSubmit={handleOnSubmit}
    >
      <div className="my-3">
        {step === 'email' && (
          <InputWithIcon
            id="email-input"
            className="mb-4"
            placeholder="Informe seu e-mail"
            label="E-mail"
            name="email"
            type="email"
            prependIcon={faEnvelope}
            value={values?.email}
            errorText={errors?.email}
            onChange={({ target }) => {
              setFieldValue(target.name, target.value.trim());
            }}
            autoComplete="username"
          />
        )}

        {step === 'password' && (
          <Button
            variant="outline"
            icon={faEnvelope}
            appendIcon={faPenToSquare}
            className="mb-4 rounded-md"
            ellipsize
            onClick={() => {
              setStep('email');
              setFieldValue('password', '');
            }}
          >
            {values.email}
          </Button>
        )}

        {step === 'password' && isFederatedUser === false && (
          <PasswordInput
            id="password-input"
            className="mb-2"
            placeholder="Digite sua senha"
            label="Senha"
            name="password"
            prependIcon={faLock}
            value={values?.password}
            errorText={touched.password ? errors?.password : undefined}
            onChange={({ target }) => {
              setFieldTouched(target.name);
              setFieldValue(target.name, target.value.trim());
            }}
            autoComplete="current-password"
          />
        )}

        {step === 'password' &&
          !isFederatedUser &&
          !isPasswordResetBlocked(values.email) && (
            <div className="d-flex justify-content-end mb-4">
              <Button
                variant="link"
                size="sm"
                onClick={() => onUpdatePassword(values.email)}
              >
                Esqueci a senha
              </Button>
            </div>
          )}
      </div>

      {!loading && step === 'password' && (
        <div className="d-flex flex-column flex-items-end flex-justify-center mt-3">
          {userFederations?.map((federation) => (
            <Button
              key={federation.federationId}
              disabled={!federation.enabled || loading}
              onClick={() => federatedSignIn(federation.federationId)}
              size="lg"
            >
              Entrar com {federation.federationName}
            </Button>
          ))}
        </div>
      )}

      {!loading && step === 'email' && (
        <div className="d-flex flex-items-center flex-justify-end mt-3">
          <Button
            size="lg"
            type="button"
            disabled={loading}
            onClick={() => {
              onSubmitEmail?.(values.email, () => setStep('password'));
            }}
          >
            Próximo
          </Button>
        </div>
      )}

      {!loading && step === 'password' && isFederatedUser === false && (
        <div className="d-flex flex-items-center flex-justify-end mt-3">
          <Button size="lg" type="submit" disabled={loading}>
            Entrar
          </Button>
        </div>
      )}
    </form>
  );
};

export default LoginForm;
